import { useQuery } from '@apollo/client';
import { CircularProgress, Icon } from '@elipssolution/harfang';
import { mdiTrendingDown, mdiTrendingNeutral, mdiTrendingUp } from '@mdi/js';
import { Stack, Typography } from '@mui/material';
import numeral from 'numeral';
import { useMemo } from 'react';

import { FETCH_REVENUE, FetchRevenueType } from '../../../../../src/api/revenue';
import { PeriodicityEnum } from '../../../../../src/types/revenue';
import { WidgetComponentProps } from '../../../../../types/widget';
import { WidgetRevenueConfigurationType } from '../configuration/WidgetRevenueConfiguration';
import PeriodBanner from '../PeriodBanner';
import { calculatePercentageChange } from '../utils/calculatePercentageChange';

const TODAY_DATE = new Date();

type MobileWidgetRevenueProps = {
	configuration: WidgetComponentProps['configuration'];
};

const MobileWidgetRevenue = ({ configuration }: MobileWidgetRevenueProps) => {
	const {
		accountRanges,
		isBalanceCumulative = false,
		periodicity = PeriodicityEnum.ANNUALLY,
		formula,
	} = (configuration as WidgetRevenueConfigurationType | undefined) ?? {};

	const { data: revenueData, loading: isRevenueLoading } = useQuery<FetchRevenueType>(FETCH_REVENUE, {
		skip: !periodicity,
		variables: {
			revenueInput: {
				rangeAccount: accountRanges?.map(({ value }) => value),
				isBalanceCumulative,
				date: TODAY_DATE,
				periodicity,
				formula,
			},
		},
	});

	const {
		revenue: { balance = '0', previousBalance = '0' },
	} = revenueData || { revenue: {} };

	const percentageChange = calculatePercentageChange({ previousBalance, currentBalance: balance });

	const percentageIcon = useMemo(() => {
		if (percentageChange === 0) return mdiTrendingNeutral;

		return percentageChange > 0 ? mdiTrendingUp : mdiTrendingDown;
	}, [percentageChange]);

	return (
		<Stack flex={1}>
			{isRevenueLoading ? (
				<Stack alignItems="center" justifyItems="center" paddingBottom={1}>
					<CircularProgress color="inherit" />
				</Stack>
			) : (
				<Stack gap={2}>
					<PeriodBanner
						balance={balance}
						date={TODAY_DATE}
						periodicity={periodicity}
						previousBalance={previousBalance}
					/>

					{numeral(previousBalance).value() !== 0 && (
						<Stack flexDirection="row" justifyContent="center" alignItems="center" gap={2}>
							<Icon path={percentageIcon} size="large" />

							<Typography fontWeight="bold" variant="h6">
								{numeral(percentageChange).format('0,00.00')}%
							</Typography>
						</Stack>
					)}
				</Stack>
			)}
		</Stack>
	);
};

export default MobileWidgetRevenue;
